import React, { useState, useMemo } from 'react';
import {
  ShowButton,
} from 'react-admin';
import {
  Card,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField as MuiTextField,
  IconButton,
} from '@mui/material';
import {
  Task as TaskIcon,
  Delete as DeleteIcon,
  LocationOn as ZoneIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import PaginationControls from '@/components/PaginationControls';
import DeleteWithConfirmationDialog from '@/components/DeleteWithConfirmationDialog';
import {
  getGetApiVariablesTasksQueryKey,
  getApiVariablesTasks,
  getApiZones
} from '@/api/gen/api';
import TableComponent, { Column } from '@/components/TableComponent';
import cronstrueWrapper from '@/components/cronstrueWrapper';

// Función para formatear expresiones cron
const formatCronExpression = (cronExpression: string | undefined): string => {
  if (!cronExpression) return "—";
  try {
    const description = cronstrueWrapper.toString(cronExpression, {
      locale: "es",
      dayOfWeekStartIndexZero: false,
      verbose: false,
    });
    return description.replace(/^En /, "").replace(/^A las /, "");
  } catch {
    return "Custom";
  }
};

// DebouncedSearchInput simplificado
const DebouncedSearchInput = React.memo(({
  onSearchChange,
  initialValue = ''
}: {
  onSearchChange: (value: string) => void;
  initialValue?: string;
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const debounceRef = React.useRef<NodeJS.Timeout>();

  const handleChange = (value: string) => {
    setInputValue(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onSearchChange(value);
    }, 500);
  };

  React.useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  return (
    <MuiTextField
      size="small"
      placeholder="Buscar tasks..."
      value={inputValue}
      onChange={(e) => handleChange(e.target.value)}
      sx={{ minWidth: 200, mb: 0.5 }}
    />
  );
});

const pageSizeOptions = [5, 10, 20, 50];

const buttonStyle = {
  minWidth: '24px',
  width: '24px',
  height: '24px',
  padding: '2px',
  '& .MuiButton-startIcon': { margin: 0 },
  '& svg': { fontSize: '16px' }
};

export const TasksList = () => {
  // Estados simplificados
  const [filterValues, setFilterValues] = useState<HierarchicalFilterSelections>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, perPage: 5 });

  // Estado para el diálogo de eliminación
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    record: null as any,
  });

  // Construir parámetros para la query
  const queryParams = useMemo(() => {
    const params: any = {
      page: pagination.page,
      pageSize: pagination.perPage
    };

    if (searchQuery.trim()) params.SearchString = searchQuery;
    if (filterValues.plantId) params.PlantId = filterValues.plantId;
    if (filterValues.zoneId) params.ZoneId = filterValues.zoneId;

    return params;
  }, [filterValues, pagination, searchQuery]);

  // Query key simplificado
  const queryKey = getGetApiVariablesTasksQueryKey(queryParams);

  // Hook de Orval para tasks
  const {
    data: tasksResponse,
    isLoading: isLoadingTasks,
    error: tasksError,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () => getApiVariablesTasks(queryParams),
    retry: false,
    refetchOnWindowFocus: false,
  });

  //  Query para zonas con useMemo 
  const zonesQueryParams = useMemo(() => ({
    isPaginated: false,
    page: 1,
    pageSize: 1000
  }), []);

  const { data: zonesResponse, isLoading: isLoadingZones } = useQuery({
    queryKey: ['zones-all', zonesQueryParams],
    queryFn: () => getApiZones(zonesQueryParams),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Mapa de zonas
  const zonesMap = useMemo(() => {
    if (!zonesResponse || !Array.isArray(zonesResponse)) return new Map();

    const map = new Map();
    zonesResponse.forEach((zone: any) => {
      if (zone && zone.id) {
        const zoneName = zone.zoneName || zone.name || `Zona ${zone.id}`;
        map.set(zone.id, zoneName);
      }
    });

    return map;
  }, [zonesResponse]);

  // Extrae datos
  const { tasks, totalCount, showPagination } = useMemo(() => {
    if (!tasksResponse) return { tasks: [], totalCount: 0, showPagination: false };

    const items = tasksResponse.items || [];
    const total = tasksResponse.totalCount || 0;

    return {
      tasks: items,
      totalCount: total,
      showPagination: total > pagination.perPage
    };
  }, [tasksResponse, pagination.perPage]);

  // Handlers para eliminación
  const handleOpenDeleteDialog = (record: any) => {
    setDeleteDialog({
      open: true,
      record,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      record: null,
    });
  };

  // Handlers 
  const handleFilterChange = React.useCallback((selections: HierarchicalFilterSelections) => {
    console.log('🔄 Filter changed:', selections);
    setFilterValues(selections);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearchQuery(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination({ perPage, page: 1 });
  };

  // Columnas 
  const taskColumns: Column[] = [
    {
      key: 'id',
      label: 'ID',
      width: '35px',
    },
    {
      key: 'variableName',
      label: 'Nombre',
      maxWidth: '140px',
    },
    {
      key: 'description',
      label: 'Descripción',
      maxWidth: '180px',
    },
    {
      key: 'calculationType',
      label: 'Tipo Cálculo',
      maxWidth: '120px',
      render: (value) => (
        <Chip
          label={value || 'No definido'}
          color="primary"
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      key: 'cronExpression',
      label: 'Programación',
      maxWidth: '130px',
      render: (value) => formatCronExpression(value), 
    },
    {
      key: 'zoneId',
      label: 'Zona',
      maxWidth: '90px',
      render: (value, record) => {
        const zoneName = zonesMap.get(record.zoneId);

        if (isLoadingZones) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={12} />
              <span>Cargando...</span>
            </Box>
          );
        }

      
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ZoneIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
            <Link
              to={`/zones/${record.zoneId}/show`}
              style={{
                color: 'inherit',
                cursor: 'pointer'
              }}
              onClick={(e) => e.stopPropagation()} 
            >
              <Typography
                variant="body2"
                sx={{
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline'
                  }
                }}
              >
                {zoneName || `Zona ${record.zoneId}`}
              </Typography>
            </Link>
          </Box>
        );
      },
    },
    {
      key: 'isActive',
      label: 'Estado',
      maxWidth: '80px',
      render: (value) => (
        <Chip
          label={value !== false ? 'Activa' : 'Inactiva'}
          color={value !== false ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '95px',
      align: 'center',
      render: (value, record) => (
        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <ShowButton record={record} resource='task' sx={buttonStyle} label={''} />
          <IconButton
            size="small"
            onClick={() => handleOpenDeleteDialog(record)}
            sx={buttonStyle}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Card elevation={1} sx={{ m: 2, p: 2 }}>
        <Box sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h5' component='h1' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TaskIcon color='primary' />
            Gestión de Tasks
            {isFetching && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Gestiona las tasks del sistema organizadas por plantas y zonas
          </Typography>
        </Box>

        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h6' gutterBottom color='primary'>
            Filtros de Búsqueda
          </Typography>

          <HierarchicalFilter
            levels={['plant', 'zone']}
            onChange={handleFilterChange}
            showTable={false}
          />

          <Box sx={{ mt: 2 }}>
            <DebouncedSearchInput
              onSearchChange={handleSearchChange}
              initialValue={searchQuery}
            />
          </Box>

          {(filterValues.plantId || filterValues.zoneId) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                Selección actual:
                {filterValues.plantName && ` Planta: ${filterValues.plantName}`}
                {filterValues.zoneName && ` → Zona: ${filterValues.zoneName}`}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          {tasksError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error cargando tasks: {tasksError.message}
            </Alert>
          )}

          {isLoadingTasks && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!isLoadingTasks && tasks.length === 0 && (
            <Alert severity="info">No se encontraron tasks con los filtros actuales</Alert>
          )}

          {tasks.length > 0 && (
            <>
              {showPagination && (
                <PaginationControls
                  totalCount={totalCount}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  pageSizeOptions={pageSizeOptions}
                  showItemsPerPage={true}
                />
              )}

              <TableComponent
                data={tasks}
                columns={taskColumns}
                loading={isLoadingTasks}
                emptyMessage="No se encontraron tasks"
              />

              {showPagination && (
                <PaginationControls
                  totalCount={totalCount}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  pageSizeOptions={pageSizeOptions}
                  showItemsPerPage={false}
                />
              )}
            </>
          )}
        </Box>
      </Card>

      {/* Diálogo de confirmación de eliminación */}
      <DeleteWithConfirmationDialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        record={deleteDialog.record}
        resource="variables/task"
        nameField="variableName"
        displayName={deleteDialog.record?.variableName}
      />
    </>
  );
};

export default TasksList;